import { useEffect, useMemo, useState } from "react";
import { api, formatCnpj } from "../api";
import type { Company, DarfStandardGroup } from "../types";

type Flash = { kind: "ok" | "error"; text: string } | null;
type Props = { companies: Company[]; canManage: boolean; flash: (value: Flash) => void; onConfigure: () => void };

export default function DarfAssignmentsPage({ companies, canManage, flash, onConfigure }: Props) {
  const [groups, setGroups] = useState<DarfStandardGroup[]>([]);
  const [groupId, setGroupId] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  async function load(preferredId?: number) {
    const result = await api<{ items: DarfStandardGroup[] }>("/api/darf-standards");
    setGroups(result.items);
    const current = result.items.find((group) => group.id === (preferredId ?? groupId)) ?? result.items[0];
    setGroupId(current?.id ?? 0);
    setSelected(current?.company_ids ?? []);
    setDirty(false);
  }
  useEffect(() => { load().catch((error) => flash({ kind: "error", text: error.message })); }, []);

  const group = groups.find((item) => item.id === groupId);
  const visible = useMemo(() => companies.filter((company) =>
    `${company.trade_name} ${company.legal_name} ${company.cnpj}`.toLowerCase().includes(query.toLowerCase())
  ), [companies, query]);
  const selectedSet = new Set(selected);

  function changeGroup(id: number) {
    const next = groups.find((item) => item.id === id);
    setGroupId(id);
    setSelected(next?.company_ids ?? []);
    setDirty(false);
    setQuery("");
  }
  function toggle(id: number) {
    setSelected(selectedSet.has(id) ? selected.filter((value) => value !== id) : [...selected, id]);
    setDirty(true);
  }
  function setVisible(checked: boolean) {
    const next = new Set(selected);
    for (const company of visible) {
      if (checked) next.add(company.id); else next.delete(company.id);
    }
    setSelected([...next]);
    setDirty(true);
  }
  async function save() {
    if (!group) return;
    setSaving(true);
    try {
      await api(`/api/darf-standards/${group.id}/companies`, {
        method: "PUT", body: JSON.stringify({ company_ids: selected }),
      });
      await load(group.id);
      flash({ kind: "ok", text: "Empresas participantes atualizadas." });
    } catch (error) {
      flash({ kind: "error", text: error instanceof Error ? error.message : "Falha ao aplicar grupo" });
    } finally { setSaving(false); }
  }

  return <>
    <div className="page-head"><div>
      <p className="eyebrow">ADESÃO AOS GRUPOS</p>
      <h1>Aplicar grupos às empresas</h1>
      <p>Escolha um grupo configurado e defina quais empresas o utilizam.</p>
    </div><button className="secondary" onClick={onConfigure}>Configurar grupos</button></div>
    <div className="notice">Etapa 2 de 2: empresas novas não entram automaticamente. Esta associação ainda não inicia a busca por códigos.</div>
    {groups.length ? <>
      <section className="card darf-filters"><label>Grupo padrão
        <select value={groupId} onChange={(event) => changeGroup(Number(event.target.value))}>
          {groups.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label><div className="assignment-summary"><strong>{group?.revenue_codes.length ?? 0} códigos</strong><span>{selected.length} de {companies.length} empresas selecionadas</span></div></section>
      <section className="card assignment-card">
        <div className="assignment-toolbar"><input className="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar empresa ou CNPJ" />
          {canManage && <div className="actions"><button type="button" className="secondary" onClick={() => setVisible(true)}>Selecionar visíveis</button><button type="button" className="secondary" onClick={() => setVisible(false)}>Remover visíveis</button></div>}</div>
        <div className="assignment-list">{visible.map((company) => <label key={company.id} className="assignment-row">
          <input type="checkbox" disabled={!canManage} checked={selectedSet.has(company.id)} onChange={() => toggle(company.id)} />
          <span><strong>{company.trade_name || company.legal_name}</strong><small>{formatCnpj(company.cnpj)}</small></span>
          <small>{company.active ? "Ativa" : "Inativa"}</small>
        </label>)}</div>
        {!visible.length && <p className="muted">Nenhuma empresa corresponde à busca.</p>}
        {canManage && <footer className="assignment-footer"><span>{dirty ? "Alterações ainda não salvas" : "Associações salvas"}</span><button className="primary" disabled={!dirty || saving} onClick={save}>{saving ? "Salvando…" : "Salvar empresas"}</button></footer>}
      </section>
    </> : <section className="card empty"><p>Configure um grupo padrão antes de associar empresas.</p><button className="primary" onClick={onConfigure}>Configurar grupos</button></section>}
  </>;
}
